import {
  CANCEL_LISTING,
  CANCEL_LISTING_FAIL,
  CANCEL_LISTING_SUCCESS,
  CLEAR_ACTIVE_LISTING_ID,
  CONFIRM_LISTING,
  CONFIRM_LISTING_FAIL,
  CONFIRM_LISTING_SUCCESS,
  CREATE_MANUAL_LISTING,
  CREATE_MANUAL_LISTING_FAIL,
  CREATE_MANUAL_LISTING_SUCCESS,
  DELETE_IMAGE,
  DELETE_IMAGE_FAIL,
  DELETE_IMAGE_SUCCESS,
  DELETE_INPUT_DOCUMENT,
  DELETE_INPUT_DOCUMENT_FAIL,
  DELETE_INPUT_DOCUMENT_SUCCESS,
  FETCH_LISTING,
  FETCH_LISTING_FAIL,
  FETCH_LISTING_SUCCESS,
  SET_ACTIVE_LISTING_ID,
  SUBMIT_LISTING,
  SUBMIT_LISTING_FAIL,
  SUBMIT_LISTING_SUCCESS,
  UPDATE_LISTING_IMPORT,
  UPLOAD_IMAGES,
  UPLOAD_IMAGES_FAIL,
  UPLOAD_IMAGES_SUCCESS,
  UPLOAD_INPUT_DOCUMENT,
  UPLOAD_INPUT_DOCUMENT_FAIL,
  UPLOAD_INPUT_DOCUMENT_SUCCESS,
} from "../actionTypes";

/**
 * @typedef {Object} AssessmentState
 * @property {string | Record<string, string[]> | null} error
 * @property {boolean} globalLoading
 * @property {{ uuid?: string; images?: Array<{id: number|string; file?: string; url?: string}>; source?: string; [key: string]: any } | null} currentListing
 * @property {boolean} confirmationLoading
 * @property {string | null} confirmationError
 * @property {boolean} imageUploadLoading
 * @property {string | null} imageUploadError
 * @property {boolean} documentUploadLoading
 * @property {string | Record<string, string[]> | null} documentUploadError
 * @property {string | null} [importRequestId]
 * @property {string | null} activeListingId
 */

/** @type {AssessmentState} */
const assessmentInitialState = {
  error: null,
  globalLoading: false,
  // currentListing holds the full listing record whenever there is one
  // "in play" — set on submit success, fetch success, confirm, and image
  // upload/delete. The old `listingSubmission` field was a redundant
  // copy; components now watch `activeListingId` for the just-submitted
  // transition.
  currentListing: null,
  confirmationLoading: false,
  confirmationError: null,
  imageUploadLoading: false,
  imageUploadError: null,
  documentUploadLoading: false,
  documentUploadError: null,
  activeListingId: null,
  importRequestId: null,
};

/**
 * @param {AssessmentState | undefined} state
 * @param {{ type: string; payload?: any; meta?: any }} action
 * @returns {AssessmentState}
 */
const assessmentReducer = (state, action) => {
  const currentState = state ?? assessmentInitialState;
  switch (action.type) {
    case SUBMIT_LISTING: {
      const sameListing =
        action.payload?.listingUuid && action.payload.listingUuid === currentState.activeListingId;
      return {
        ...currentState,
        globalLoading: true,
        error: null,
        currentListing: sameListing ? currentState.currentListing : null,
        confirmationError: null,
        activeListingId: sameListing ? currentState.activeListingId : null,
        importRequestId: action.payload?.importRequestId || null,
      };
    }
    case SUBMIT_LISTING_SUCCESS:
      if (
        action.meta?.importRequestId &&
        action.meta.importRequestId !== currentState.importRequestId
      )
        return currentState;
      return {
        ...currentState,
        globalLoading: false,
        currentListing: action.payload,
        error: null,
        activeListingId: action.payload?.uuid ?? currentState.activeListingId,
      };
    case SUBMIT_LISTING_FAIL:
      if (
        action.meta?.importRequestId &&
        action.meta.importRequestId !== currentState.importRequestId
      )
        return currentState;
      return {
        ...currentState,
        globalLoading: false,
        error: action.payload.error,
      };
    case UPDATE_LISTING_IMPORT:
      if (
        !action.meta?.listingUuid ||
        action.meta.listingUuid !== currentState.activeListingId ||
        action.payload?.uuid !== currentState.activeListingId
      )
        return currentState;
      return {
        ...currentState,
        currentListing: { ...currentState.currentListing, ...action.payload },
        error: null,
      };
    case FETCH_LISTING:
      return {
        ...currentState,
        confirmationLoading: true,
        confirmationError: null,
      };
    case FETCH_LISTING_SUCCESS:
      return {
        ...currentState,
        confirmationLoading: false,
        currentListing: action.payload,
        confirmationError: null,
      };
    case FETCH_LISTING_FAIL: {
      // Only a definitive answer (gone / not yours) means the UUID is stale.
      // A network blip or 5xx must NOT forget the in-funnel listing — that
      // stranded users on "Listing Not Found" over a transient error.
      const definitelyStale = action.payload.status === 404 || action.payload.status === 403;
      return {
        ...currentState,
        confirmationLoading: false,
        confirmationError: action.payload.error,
        activeListingId: definitelyStale ? null : currentState.activeListingId,
        importRequestId: definitelyStale ? null : currentState.importRequestId,
      };
    }
    case CONFIRM_LISTING:
      return {
        ...currentState,
        confirmationLoading: true,
        confirmationError: null,
      };
    case CONFIRM_LISTING_SUCCESS:
      return {
        ...currentState,
        confirmationLoading: false,
        currentListing: action.payload,
        confirmationError: null,
      };
    case CONFIRM_LISTING_FAIL:
      return {
        ...currentState,
        confirmationLoading: false,
        confirmationError: action.payload.error,
      };
    case CANCEL_LISTING:
      return {
        ...currentState,
        confirmationLoading: true,
        confirmationError: null,
      };
    case CANCEL_LISTING_SUCCESS:
      return {
        ...currentState,
        importRequestId: null,
        confirmationLoading: false,
        currentListing: null,
        confirmationError: null,
        activeListingId: null,
      };
    case CANCEL_LISTING_FAIL:
      return {
        ...currentState,
        confirmationLoading: false,
        confirmationError: action.payload.error,
      };
    case CREATE_MANUAL_LISTING:
      return {
        ...currentState,
        importRequestId: null,
        globalLoading: true,
        error: null,
        currentListing: null,
        confirmationError: null,
        activeListingId: null,
      };
    case CREATE_MANUAL_LISTING_SUCCESS:
      return {
        ...currentState,
        globalLoading: false,
        currentListing: action.payload,
        error: null,
        activeListingId: action.payload?.uuid ?? currentState.activeListingId,
      };
    case CREATE_MANUAL_LISTING_FAIL:
      return {
        ...currentState,
        globalLoading: false,
        error: action.payload.error,
      };
    case UPLOAD_IMAGES:
      return {
        ...currentState,
        imageUploadLoading: true,
        imageUploadError: null,
      };
    case UPLOAD_IMAGES_SUCCESS:
      return {
        ...currentState,
        imageUploadLoading: false,
        currentListing: action.payload,
        imageUploadError: null,
      };
    case UPLOAD_IMAGES_FAIL:
      return {
        ...currentState,
        imageUploadLoading: false,
        imageUploadError: action.payload.error,
      };
    case UPLOAD_INPUT_DOCUMENT:
    case DELETE_INPUT_DOCUMENT:
      return {
        ...currentState,
        documentUploadLoading: true,
        documentUploadError: null,
      };
    case UPLOAD_INPUT_DOCUMENT_SUCCESS:
    case DELETE_INPUT_DOCUMENT_SUCCESS:
      return {
        ...currentState,
        documentUploadLoading: false,
        currentListing: action.payload,
        documentUploadError: null,
      };
    case UPLOAD_INPUT_DOCUMENT_FAIL:
    case DELETE_INPUT_DOCUMENT_FAIL:
      return {
        ...currentState,
        documentUploadLoading: false,
        documentUploadError: action.payload.error,
      };
    case DELETE_IMAGE:
      return {
        ...currentState,
        imageUploadLoading: true,
        imageUploadError: null,
      };
    case DELETE_IMAGE_SUCCESS:
      return {
        ...currentState,
        imageUploadLoading: false,
        currentListing: action.payload,
        imageUploadError: null,
      };
    case DELETE_IMAGE_FAIL:
      return {
        ...currentState,
        imageUploadLoading: false,
        imageUploadError: action.payload.error,
      };
    case SET_ACTIVE_LISTING_ID:
      return {
        ...currentState,
        activeListingId: action.payload,
        globalLoading:
          action.payload !== currentState.activeListingId && currentState.importRequestId
            ? false
            : currentState.globalLoading,
        importRequestId:
          action.payload === currentState.activeListingId ? currentState.importRequestId : null,
      };
    case CLEAR_ACTIVE_LISTING_ID:
      return {
        ...currentState,
        importRequestId: null,
        globalLoading: currentState.importRequestId ? false : currentState.globalLoading,
        activeListingId: null,
      };
    default:
      return currentState;
  }
};

export default assessmentReducer;
